import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../../config';
import authService from '../../services/authService';
import './StoriesPage.css';

interface Story {
  id: string;
  title: string;
  l0_category: string;
  l1_category: string;
  l2_category: string;
  tags: string[];
  author: string;
  duration: number;
  language: string;
  image_url: string;
  is_active: boolean;
  type: string;
  created_at: string;
  updated_at: string;
}

interface StoriesResponse {
  stories: Story[];
  total_count: number;
}

interface CreateStoryPayload {
  title: string;
  l0_category: string;
  l1_category: string;
  l2_category: string;
  tags: string[];
  author: string;
  duration: number;
  language: string;
  image_url: string;
}

const StoriesPage: React.FC<{
  onEditStory?: (story: Story) => void;
  onDeleteStory?: (storyId: string) => void;
}> = ({ onEditStory, onDeleteStory }) => {
  const [stories, setStories] = useState<Story[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all');
  const navigate = useNavigate();

  const fuzzyMatch = (str: string, query: string): boolean => {
    const strLower = str.toLowerCase();
    const queryLower = query.toLowerCase();
    let strIndex = 0;
    let queryIndex = 0;

    while (strIndex < strLower.length && queryIndex < queryLower.length) {
      if (strLower[strIndex] === queryLower[queryIndex]) {
        queryIndex++;
      }
      strIndex++;
    }
    return queryIndex === queryLower.length;
  };

  const filteredStories = [...stories]
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
    .filter(story => {
      const matchesSearch = fuzzyMatch(story.title, searchQuery);
      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'active'
            ? story.is_active
            : !story.is_active;
      return matchesSearch && matchesStatus;
    });

  const activeCount = stories.filter(s => s.is_active).length;
  const inactiveCount = stories.filter(s => !s.is_active).length;

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${config.API_BASE_URL}/story/`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stories');
      }

      const data: StoriesResponse = await response.json();
      setStories(data.stories || []);
      setTotalCount(data.total_count || 0);
    } catch (error) {
      console.error('Error fetching stories:', error);
      setError('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${config.API_BASE_URL}/story/bulk-upload`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulk-upload-result.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('Upload successful! Result file downloaded.');
      fetchStories();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="stories-loading-container">
        <div className="loading-spinner"></div>
        <span>Loading stories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stories-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="stories-container">
      <div className="stories-header">
        <h2>All Stories</h2>
        <div className="stories-header-actions">
          <span className="stories-count">Total: {totalCount}</span>
          <button
            className="upload-excel-button"
            onClick={() => {
              const headers = [
                'id',
                'title',
                'l0_category',
                'l1_category',
                'l2_category',
                'tags',
                'author',
                'duration',
                'language',
                'image_url',
                'is_active',
                'type',
                'created_at',
                'updated_at',
              ];
              const rows = stories.map(s =>
                [
                  s.id,
                  `"${(s.title || '').replace(/"/g, '""')}"`,
                  s.l0_category,
                  s.l1_category,
                  s.l2_category,
                  `"${(s.tags || []).join(', ')}"`,
                  s.author,
                  s.duration,
                  s.language,
                  s.image_url,
                  s.is_active,
                  s.type,
                  s.created_at,
                  s.updated_at,
                ].join(',')
              );
              const csv = [headers.join(','), ...rows].join('\n');
              const blob = new Blob([csv], {
                type: 'text/csv',
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'stories.csv';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Download CSV
          </button>
        </div>
      </div>
      <div className="stories-search">
        <input
          type="text"
          placeholder="Search stories by name..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="search-input"
        />
        <div className="status-filter">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All <span className="count">({totalCount})</span>
          </button>
          <button
            className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            Active <span className="count">({activeCount})</span>
          </button>
          <button
            className={`filter-btn ${statusFilter === 'inactive' ? 'active' : ''}`}
            onClick={() => setStatusFilter('inactive')}
          >
            Inactive <span className="count">({inactiveCount})</span>
          </button>
        </div>
      </div>
      {filteredStories.length > 0 ? (
        <div className="stories-grid">
          {filteredStories.map(story => (
            <div
              key={story.id}
              className="story-card"
              onClick={() => navigate(`/v3/stories/${story.id}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className="story-content">
                <div className="story-header-row">
                  <h3 className="story-title">{story.title}</h3>
                  <span
                    className={`status-badge ${story.is_active ? 'active' : 'inactive'}`}
                  >
                    {story.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p>
                  <strong>ID:</strong> {story.id}
                </p>
                <p>
                  <strong>Author:</strong> {story.author}
                </p>
                <p>
                  <strong>Duration:</strong> {story.duration} sec
                </p>
                <p>
                  <strong>Category:</strong> {story.l0_category}
                </p>
                {story.tags && story.tags.length > 0 && (
                  <p>
                    <strong>Tags:</strong> {story.tags.join(', ')}
                  </p>
                )}
              </div>
              <div className="story-actions">
                <button
                  className="edit-story-button"
                  onClick={e => {
                    e.stopPropagation();
                    onEditStory?.(story);
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit
                </button>
                <button
                  className="delete-story-button"
                  onClick={e => {
                    e.stopPropagation();
                    onDeleteStory?.(story.id);
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="3,6 5,6 21,6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="stories-empty">
          <svg
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <h3>{searchQuery ? 'No Stories Found' : 'No Stories Available'}</h3>
          <p>
            {searchQuery
              ? 'Try adjusting your search query.'
              : 'There are currently no stories to display.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default StoriesPage;
